import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea,
} from "recharts";

const tooltipStyle = {
  contentStyle: { background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "hsl(220 10% 70%)" },
};
const axis = { stroke: "hsl(220 10% 40%)", fontSize: 11, tickLine: false, axisLine: false };
const grid = <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />;
const monthsShort = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const fmtDate = (d) => (d && d.length === 7
  ? `${monthsShort[parseInt(d.slice(5, 7), 10) - 1]} ${d.slice(2, 4)}`
  : `${d.slice(8, 10)}.${d.slice(5, 7)}`);
const clickProps = (onDayClick) => ({
  onClick: (e) => e?.activeLabel && onDayClick?.(e.activeLabel),
  style: { cursor: onDayClick ? "pointer" : "default" },
});

export function RecoveryChart({ data, onDayClick, norm }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis domain={[0, 100]} {...axis} width={30} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        {norm && <ReferenceArea y1={norm.low} y2={norm.high} fill="#ffffff" fillOpacity={0.05} stroke="#8899a6" strokeOpacity={0.25} strokeDasharray="4 4" label={{ value: "ваша норма", position: "insideTopRight", fontSize: 9, fill: "hsl(220 10% 45%)" }} />}
        <Area type="monotone" dataKey="recovery" name="Восстановление %" stroke="#00e676" strokeWidth={2} fill="#00e676" fillOpacity={0.15} connectNulls />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function HrvRhrChart({ data, onDayClick, normHrv }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis yAxisId="hrv" {...axis} width={30} />
        <YAxis yAxisId="rhr" orientation="right" {...axis} width={30} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {normHrv && <ReferenceArea yAxisId="hrv" y1={normHrv.low} y2={normHrv.high} fill="#0084ff" fillOpacity={0.06} stroke="#0084ff" strokeOpacity={0.2} strokeDasharray="4 4" />}
        <Line yAxisId="hrv" type="monotone" dataKey="hrv" name="HRV, мс" stroke="#0084ff" strokeWidth={2} dot={false} connectNulls />
        <Line yAxisId="rhr" type="monotone" dataKey="rhr" name="Пульс покоя" stroke="#ff3b30" strokeWidth={2} dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function SleepStagesChart({ data, onDayClick }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis {...axis} width={30} unit="ч" />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="deep_h" name="Глубокий" stackId="s" fill="#3d5afe" />
        <Bar dataKey="rem_h" name="REM" stackId="s" fill="#a970ff" />
        <Bar dataKey="light_h" name="Лёгкий" stackId="s" fill="#4dabf5" />
        <Bar dataKey="awake_h" name="Бодрствование" stackId="s" fill="hsl(220 10% 30%)" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SleepQualityChart({ data, onDayClick }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis domain={[0, 100]} {...axis} width={30} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line type="monotone" dataKey="sleep_performance" name="Эффективность %" stroke="#a970ff" strokeWidth={2} dot={false} connectNulls />
        <Line type="monotone" dataKey="sleep_consistency" name="Стабильность %" stroke="#ffea00" strokeWidth={2} dot={false} connectNulls />
        <Line type="monotone" dataKey="sleep_efficiency" name="Качество %" stroke="#00e676" strokeWidth={2} dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function StrainRecoveryChart({ data, onDayClick }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis yAxisId="strain" domain={[0, 21]} {...axis} width={30} />
        <YAxis yAxisId="rec" orientation="right" domain={[0, 100]} {...axis} width={30} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="strain" dataKey="strain" name="Нагрузка" fill="#0084ff" fillOpacity={0.7} radius={[3, 3, 0, 0]} />
        <Line yAxisId="rec" type="monotone" dataKey="recovery" name="Восстановление %" stroke="#00e676" strokeWidth={2} dot={false} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function SleepTimesChart({ data, onDayClick }) {
  const rows = data.map((d) => {
    if (d.bed_h == null || d.wake_h == null) return { date: d.date };
    const start = (d.bed_h - 18 + 24) % 24;
    let end = (d.wake_h - 18 + 24) % 24;
    if (end <= start) end = start + ((d.sleep_h || 0) + (d.awake_h || 0)) || start + 1;
    return { date: d.date, window: [Math.round(start * 100) / 100, Math.round(end * 100) / 100] };
  });
  const fmtH = (v) => `${String(Math.floor((18 + v) % 24)).padStart(2, "0")}:00`;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis domain={[0, 24]} ticks={[0, 3, 6, 9, 12, 15, 18, 21, 24]} tickFormatter={fmtH} {...axis} width={44} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate}
          formatter={(v) => (Array.isArray(v) ? `${fmtH(v[0]).slice(0, 5)} → ${fmtH(v[1])}` : v)} />
        <Bar dataKey="window" name="Отбой → подъём" fill="#a970ff" fillOpacity={0.7} radius={[3, 3, 3, 3]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CaloriesChart({ data, onDayClick }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} {...clickProps(onDayClick)}>
        {grid}
        <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
        <YAxis {...axis} width={40} />
        <Tooltip {...tooltipStyle} labelFormatter={fmtDate} />
        <Bar dataKey="kcal" name="Калории, ккал" fill="#ffea00" fillOpacity={0.75} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
